import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const generalCapabilities = [
  '概括课堂内容',
  '回答提问',
  '生成建议',
  '灵活的语言与理解能力',
];

const professionalCapabilities = [
  { label: '长视频切片', desc: '40-45分钟有效切片与角色识别' },
  { label: '多模态对齐', desc: '文本、声音、画面三类互证' },
  { label: '知识图谱', desc: '学科知识底座+教学逻辑图谱' },
  { label: '专家样本', desc: '指标映射与专业判断学习' },
  { label: '多维输出', desc: '总分/维度分/分档/证据链' },
  { label: '群体分析', desc: '教师成长→学校质量→区域趋势' },
];

export default function P44Comparison({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-12">
        <div ref={titleRef} className="text-center mb-5 md:mb-7">
          <span className="inline-block text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-3 transition-all duration-700">定位</span>
          <h2 className="font-heading text-2xl md:text-4xl lg:text-[2.75rem] font-bold text-foreground-950 transition-all duration-1000 delay-100">
            通用大模型点评与<span className="text-primary-600">专业评课系统</span>的差别
          </h2>
        </div>

        {/* 左右对比 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto mb-5 transition-all duration-1000 delay-200">
          {/* 通用大模型 */}
          <div className="p-5 rounded-2xl glass border-white/50">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-secondary-100/50 flex items-center justify-center">
                <i className="ri-robot-line text-lg text-secondary-600" />
              </div>
              <h3 className="font-heading text-sm font-bold text-foreground-800">通用大模型</h3>
            </div>
            <div className="space-y-2">
              {generalCapabilities.map((cap) => (
                <div key={cap} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background-50/60">
                  <i className="ri-check-line text-secondary-500 text-xs" />
                  <span className="text-xs text-foreground-600 font-body">{cap}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-background-200/60">
              <p className="text-[10px] text-foreground-400 font-body">
                提供灵活的语言与理解能力，但缺少标准、样本、证据、验证和汇总的工程化链路
              </p>
            </div>
          </div>

          {/* 专业评课系统 */}
          <div className="p-5 rounded-2xl glass-liquid border-primary-200/30">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl glass-blue border-primary-200/40 flex items-center justify-center">
                <i className="ri-brain-line text-lg text-primary-600" />
              </div>
              <h3 className="font-heading text-sm font-bold text-foreground-800">专业评课系统</h3>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {professionalCapabilities.map((cap) => (
                <div key={cap.label} className="p-2.5 rounded-lg glass-blue border-primary-200/30">
                  <p className="text-[11px] font-body font-bold text-primary-700">{cap.label}</p>
                  <p className="text-[10px] text-foreground-500 font-body mt-0.5">{cap.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-primary-100/50">
              <p className="text-[10px] text-foreground-500 font-body">
                把通用模型的语言与理解能力，组织成按标准运行、按证据解释、按结果验证的评价工作流
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto text-center transition-all duration-1000 delay-600">
          <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl glass-gold border-accent-200/30">
            <i className="ri-information-line text-accent-500 text-sm" />
            <span className="text-xs font-body text-foreground-500">
              差别主要在应用目标、数据基础和工程体系，不贬低通用模型
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}